import { getBakedWidgetApiBase } from './default-api-base.js';
import { getBakedReverbDefaults } from './default-reverb.js';

export type ScriptTagConfig = {
  /** Company slug from `data-slug` */
  slug: string | null;
  /** Use mock settings (`data-mock`) */
  mock: boolean;
  /** API origin, e.g. `https://api.example.com` (`data-api-base` or legacy `data-url`) */
  apiBase: string;
  /** Laravel module type (default `300`) */
  moduleType: string;
  /** Reverb app key (`data-reverb-key` or baked `VITE_REVERB_APP_KEY`) */
  reverbKey: string | null;
  /** Reverb WS host (`data-reverb-host` or baked `VITE_REVERB_HOST`) */
  reverbHost: string | null;
  /** Reverb port (`data-reverb-port` or baked `VITE_REVERB_PORT`) */
  reverbPort: string | null;
  /** `http` | `https` (`data-reverb-scheme` or baked `VITE_REVERB_SCHEME`) */
  reverbScheme: string | null;
  /** Verbose WebSocket logs in console (`data-ws-debug`) */
  wsDebug: boolean;
  /** Verbose image pipeline logs (`data-image-debug`) */
  imageDebug: boolean;
  /** Verbose chat scroll logs (`data-scroll-debug`) */
  scrollDebug: boolean;
  /** Verbose chat sync / reconcile logs (`data-sync-debug`) */
  syncDebug: boolean;
  /** Exit intent + context popup trigger logs (`data-popup-debug`) */
  popupDebug: boolean;
  /** POST analytics batch to `/api/widget/events` (`data-analytics-backend`, default on) */
  analyticsBackend: boolean;
  /** Analytics envelope + flush logs (`data-analytics-debug`) */
  analyticsDebug: boolean;
  /**
   * Soft human-gate for visits + proactive greeting (`data-human-gate`).
   * Default on; set `data-human-gate="false"` to restore legacy immediate behavior.
   */
  humanGate: boolean;
  scriptElement: HTMLScriptElement;
};

function normalizeApiBaseAttr(raw: string | null | undefined): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return '';
  if (trimmed === 'auto' || trimmed === '.') {
    if (typeof window !== 'undefined') {
      return window.location.origin.replace(/\/$/, '');
    }
    return '';
  }
  return trimmed.replace(/\/$/, '');
}

function resolveApiBase(script: HTMLScriptElement): string {
  const fromAttr =
    script.dataset.apiBase?.trim() ||
    script.getAttribute('data-url')?.trim() ||
    '';

  const normalized = normalizeApiBaseAttr(fromAttr);
  if (normalized) return normalized;

  const baked = getBakedWidgetApiBase();
  if (baked) return baked;

  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/\/$/, '');
  }

  return '';
}

function resolveReverbFromScript(script: HTMLScriptElement): {
  reverbKey: string | null;
  reverbHost: string | null;
  reverbPort: string | null;
  reverbScheme: string | null;
} {
  const baked = getBakedReverbDefaults();
  const key = script.dataset.reverbKey?.trim() || baked.key || null;
  const host = script.dataset.reverbHost?.trim() || baked.host || null;
  const port = script.dataset.reverbPort?.trim() || baked.port || null;
  const schemeRaw =
    script.dataset.reverbScheme?.trim() || baked.scheme || null;
  const scheme =
    schemeRaw === 'http' || schemeRaw === 'https' ? schemeRaw : null;

  return {
    reverbKey: key,
    reverbHost: host,
    reverbPort: port,
    reverbScheme: scheme,
  };
}

/** Read embed options from the loader `<script>` tag. */
export function parseScriptConfig(script: HTMLScriptElement): ScriptTagConfig {
  const mockAttr = script.dataset.mock;
  const mock = mockAttr === 'true' || mockAttr === '';
  const reverb = resolveReverbFromScript(script);

  return {
    slug: script.dataset.slug?.trim() || null,
    mock,
    apiBase: resolveApiBase(script),
    moduleType: script.dataset.moduleType?.trim() || '300',
    reverbKey: reverb.reverbKey,
    reverbHost: reverb.reverbHost,
    reverbPort: reverb.reverbPort,
    reverbScheme: reverb.reverbScheme,
    wsDebug: script.dataset.wsDebug === 'true' || script.dataset.wsDebug === '',
    imageDebug:
      script.dataset.imageDebug === 'true' || script.dataset.imageDebug === '',
    scrollDebug:
      script.dataset.scrollDebug === 'true' || script.dataset.scrollDebug === '',
    syncDebug:
      script.dataset.syncDebug === 'true' || script.dataset.syncDebug === '',
    popupDebug:
      script.dataset.popupDebug === 'true' || script.dataset.popupDebug === '',
    analyticsBackend: script.dataset.analyticsBackend !== 'false',
    analyticsDebug:
      script.dataset.analyticsDebug === 'true' ||
      script.dataset.analyticsDebug === '',
    humanGate: script.dataset.humanGate !== 'false',
    scriptElement: script,
  };
}
